import SpinnerIcon from "@icons/spinner";
import { SVGElement } from "@ui/types";
import classNames from "classnames";
import React, { ForwardedRef, ReactNode } from "react";

export type ButtonProps = {
  type?: "button" | "submit";
  onClick?: () => void;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
  iconLeft?: SVGElement;
  iconRight?: SVGElement;
  rounded?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  inline?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      onClick,
      loading = false,
      loadingText = "Processing",
      disabled = false,
      className,
      children,
      rounded: _rounded,
      size = "md",
      inline: _inline,
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    return (
      <button
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={classNames(`btn btn--${size}`, className)}
        ref={ref}
      >
        {!loading && props.iconLeft && (
          <props.iconLeft className={classNames("-ml-1 mr-2 h-5 w-5")} aria-hidden="true" />
        )}
        {loading ? (
          <>
            <SpinnerIcon
              className={classNames("w-4 h-4 mr-2 animate-spin", {
                "w-4 h-4 mr-2": size === "sm",
                "w-5 h-5 mr-3": !size || size === "md",
                "w-6 h-6 mr-4": size === "lg",
              })}
            />
            <span className="opacity-90">{loadingText}</span>
          </>
        ) : (
          children
        )}
        {!loading && props.iconRight && (
          <props.iconRight className={classNames("ml-2 -mr-1 h-5 w-5")} aria-hidden="true" />
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export const ButtonSecondary = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <Button
        {...props}
        className={classNames(
          "bg-transparent border-2 border-purple-600  hover:border-purple-700 shadow-sm text-purple-600 focus:ring-purple-500",
          "dark:text-purple-500 dark:border-purple-500 dark:hover:text-purple-500 dark:hover:border-purple-500",
          className,
        )}
        ref={ref}
      >
        {children}
      </Button>
    );
  },
);

ButtonSecondary.displayName = "ButtonSecondary";

export const ButtonPrimary = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <Button {...props} className={classNames("btn--primary", className)} ref={ref}>
        {children}
      </Button>
    );
  },
);

ButtonPrimary.displayName = "ButtonPrimary";

export const ButtonTertiary = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <Button
        {...props}
        className={classNames(
          "bg-transparent border-transparent text-purple-600 focus:ring-purple-500",
          "dark:text-purple-400 dark:hover:text-purple-500",
          className,
        )}
        ref={ref}
        inline={props?.inline ?? true}
      >
        {children}
      </Button>
    );
  },
);

ButtonTertiary.displayName = "ButtonTertiary";

export const ButtonDanger = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    return (
      <Button
        {...props}
        className={classNames(
          "bg-red-500 border-transparent shadow-sm text-white hover:bg-red-700  focus:ring-red-500",
          "dark:bg-red-600 dark:hover:bg-red-700",
          className,
        )}
        ref={ref}
      >
        {children}
      </Button>
    );
  },
);

ButtonDanger.displayName = "ButtonDanger";
